import React, { useEffect, useRef } from 'react'
import * as R from 'remeda'
import { Tooltip } from '../../old/tooltip'
import { SearchItemManager } from './SearchItem'
import { getCategoryControlData } from './hooks/useSelectWithCategoryControl'
import { SearchItemInner } from './SearchItemInner'
import { OptionProps, SelectProps, State } from './types'

interface Props<ValueType> extends Omit<SelectProps<ValueType>, 'cursor'> {
  changeHoverState: (index?: number) => void
  changeVisibility: (newVisibility: boolean) => void
  cursor: number
  index: number
  item: React.ReactElement<OptionProps>
  optionsProps: OptionProps<ValueType>[]
  optionsValues: ValueType[]
  scrolledIndex: number
  searchValue: string
  selectItem: (index: number) => void
  setState: React.Dispatch<React.SetStateAction<State>>
}

export const MappedOption: <ValueType>(props: Props<ValueType>) => React.ReactElement | null = ({
  item,
  index,
  searchValue,
  cursor,
  changeVisibility,
  changeHoverState,
  isFeSearch,
  value,
  optionsProps,
  onChange,
  search,
  stayOpenOnSelect,
  isMultiSelect,
  id,
  selectItem,
  optionsValues,
  setState,
  valueWhenAllOptionsAreCleared,
  scrolledIndex,
}) => {
  const ref = useRef<HTMLLIElement>(null)
  const onCategoryControlClick = (categoryValues: string[], checked: boolean) => {
    const values = value as unknown as string[]
    const newValues = checked
      ? R.unique([...values, ...categoryValues])
      : R.difference(values, categoryValues)
    onChange(newValues as any)
  }

  const onHoverActive = (fn: () => void) => {
    setState(s => ({
      ...s,
      addCurrentItem: fn,
    }))
  }

  const onDropdownItemClick = (optionValue: string) => {
    if (isMultiSelect) {
      const values = value as unknown as string[]
      let newValues = [...values]
      const idx = values.findIndex(val => optionValue === val)
      if (idx !== -1) {
        newValues.splice(idx, 1)
      } else {
        newValues.push(optionValue)
      }
      // Handle edge cases for removing clear all value and adding it
      if (valueWhenAllOptionsAreCleared) {
        if (newValues.length > 1) {
          newValues = newValues.filter(
            val => val !== (valueWhenAllOptionsAreCleared as string[])[0]
          )
        } else if (newValues.length === 0) {
          newValues = valueWhenAllOptionsAreCleared as unknown as string[]
        }
      }
      onChange(newValues as any)
    } else {
      // clear the search value if an option selected and dropdown not closing
      if (search && stayOpenOnSelect) {
        setState(s => ({
          ...s,
          searchValue: '',
        }))
        search('')
      }

      const index = optionsValues.findIndex(x => (x as unknown as string) === optionValue)
      selectItem(index)
    }
  }
  const {
    tip,
    value: optionValue,
    disabled,
    tipOffset,
    isWithBottomDivider,
    isCategoryLabel,
    isCategoryControl,
    hoverBackgroundColor,
    hoverColor,
    backgroundColor,
    category,
    onClickCustom,
    hideDropdownOnclick,
    hidden,
    sticky,
    searchValue: optionSearchValue,
    customContentOpener,
  } = item.props

  useEffect(() => {
    if (scrolledIndex === index) {
      ref.current?.scrollIntoView({ block: 'nearest' })
    }
  }, [scrolledIndex, index])

  if (isFeSearch && searchValue && optionSearchValue) {
    const isFilteredOut = !optionSearchValue.toLowerCase().includes(searchValue.toLowerCase())

    if (isFilteredOut) {
      return null
    }
  }

  const onMouseEnter = disabled || isCategoryLabel ? undefined : () => changeHoverState(index)
  const onMouseLeave = disabled || isCategoryLabel ? undefined : () => changeHoverState(undefined)

  const getMultiselectArray = (value: unknown) =>
    isMultiSelect && Array.isArray(value) ? value : []
  const multiSelectValue = getMultiselectArray(value)
  const multiSelectOptionProps = getMultiselectArray(optionsProps)

  const { isCategoryControlChecked, categoryValues } = getCategoryControlData({
    category,
    isCategoryControl,
    optionsProps: multiSelectOptionProps,
    multiSelectValue,
  })

  const handleClick = () => {
    if (disabled || isCategoryLabel) {
      return
    } else if (onClickCustom) {
      if (hideDropdownOnclick) {
        changeVisibility(false)
      }
      onClickCustom()
    } else if (isCategoryControl) {
      onCategoryControlClick(categoryValues, !isCategoryControlChecked)
    } else {
      if (customContentOpener) {
        setState(old => ({ ...old, showCustomContentSection: true }))
      }
      onDropdownItemClick(optionValue)
    }
  }

  const isChecked = multiSelectValue.includes(optionValue)

  const searchItem = (
    <SearchItemManager
      ref={ref}
      hidden={hidden}
      onFocus={() => {
        setState(s => ({
          ...s,
          cursor: index,
        }))
      }}
      sticky={isCategoryControl && sticky}
      tabIndex={hidden ? -1 : 0}
      onHoverActive={onHoverActive}
      role='option'
      aria-selected={isChecked}
      aria-disabled='false'
      data-test-id={`select:option:${id}:${optionValue}`}
      data-value={optionValue}
      hover={!disabled && !isCategoryLabel && cursor === index}
      active={value === optionValue}
      checked={isChecked}
      disabled={disabled}
      isCategoryControl={isCategoryControl}
      isCategoryLabel={isCategoryLabel}
      isMultiSelect={isMultiSelect}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      onKeyPress={e => {
        if (onClickCustom && e.key === 'Enter') {
          return
        }

        if (cursor === index) {
          handleClick()
        }
      }}
      boxShadow={isWithBottomDivider ? 'border/list-item' : undefined}
      isWithBottomDivider={isWithBottomDivider}
      hoverBackgroundColor={hoverBackgroundColor}
      hoverColor={hoverColor}
      backgroundColor={backgroundColor}
    >
      <SearchItemInner
        isMultiSelect={isMultiSelect}
        item={item}
        multiSelectValue={multiSelectValue}
        optionsProps={multiSelectOptionProps}
      />
    </SearchItemManager>
  )

  if (tip) {
    return (
      <Tooltip
        tip={tip}
        place='bottom'
        delay={200}
        offset={tipOffset}
        popperOptions={{
          modifiers: [
            {
              name: 'preventOverflow',
              enabled: false,
            },
          ],
        }}
      >
        {searchItem}
      </Tooltip>
    )
  }
  return searchItem
}
